import type { RouteObject } from "react-router";
import AuthCubeShell from "../features/auth/AuthCubeShell";
import FirstAdministratorSetupPage from "../features/auth/FirstAdministratorSetupPage";
import ForgotPasswordPage from "../features/auth/ForgotPasswordPage";
import InvitationAcceptancePage from "../features/auth/InvitationAcceptancePage";
import RegisterPage from "../features/auth/RegisterPage";
import ResetPasswordPage from "../features/auth/ResetPasswordPage";
import WaitingForApprovalPage from "../features/auth/WaitingForApprovalPage";
import VerifyEmailPage from "../features/auth/VerifyEmailPage";
import ConnectorsPage from "../features/connectors/management/ConnectorsPage";
import ConnectorPairingPage from "../features/connectors/pairing/ConnectorPairingPage";
import ConnectionCallbackPage from "../features/profile/connections/ConnectionCallbackPage";
import ConnectorSetupPage from "../features/connectors/setup/ConnectorSetupPage";
import ReleasePage from "../features/knowledgeData/releases/ReleasePage";
import ResearchSpaceAdministrationPage from "../features/knowledgeData/releases/ResearchSpaceAdministrationPage";
import ExtractionPage from "../features/knowledgeData/extractions/ExtractionPage";
import OntologyPage from "../features/ontology/OntologyPage";
import TermsPage from "../features/ontology/page/TermsPage";
import AccountsPage from "../features/administration/AccountsPage";
import AdministrationLayout from "../features/administration/AdministrationLayout";
import DeletedProjectsPage from "../features/administration/DeletedProjectsPage";
import InstallationGroupPage from "../features/administration/groups/InstallationGroupPage";
import InstallationGroupsPage from "../features/administration/groups/InstallationGroupsPage";
import InstallationSettingsPage from "../features/administration/InstallationSettingsPage";
import ProfileLayout from "../features/profile/ProfileLayout";
import PreferencesPage from "../features/profile/preferences/PreferencesPage";
import MyMemoriesPage from "../features/memories/MyMemoriesPage";
import ImportProposalPage from "../features/importProposals/ImportProposalPage";
import ItemPage from "../features/items/ItemPage";
import NewArtifactPage from "../features/processAuthoring/artifacts/NewArtifactPage";
import NewLabelListPage from "../features/processAuthoring/labelLists/NewLabelListPage";
import NewSkillPage from "../features/processAuthoring/skills/NewSkillPage";
import NewCapabilityOverridesPage from "../features/processAuthoring/capabilities/NewCapabilityOverridesPage";
import NewStepPage from "../features/processAuthoring/steps/NewStepPage";
import ProfilePage from "../features/profile/ProfilePage";
import { helpHandle } from "../features/help/helpArticles";
import LegacyGuideRedirect from "../features/help/LegacyGuideRedirect";
import ServerKeysPage from "../features/serverKeys/ServerKeysPage";
import ReviewPage from "../features/reviews/page/ReviewPage";
import SourceDocumentPage from "../features/sources/document/SourceDocumentPage";
import SourcesIndexPage from "../features/sources/SourcesIndexPage";
import ThreadsHome from "../features/threads/list/ThreadsHome";
import BatchVitaPage from "../features/batches/BatchVitaPage";
import BatchPage from "../features/batches/BatchPage";
import ThreadPage from "../features/threads/page/ThreadPage";
import RunGraphPage from "../features/threads/runGraph/RunGraphPage";
import ThreadPreparationPage from "../features/threads/prepare/ThreadPreparationPage";
import WorkflowPage from "../features/workflows/page/WorkflowPage";
import WorkflowsIndexPage from "../features/workflows/library/WorkflowsIndexPage";
import { asDetour } from "../shell/detour/detourRoutes";
import AllProjectsPage from "../projects/list/AllProjectsPage";
import ProjectAccessPage from "../projects/settings/access/ProjectAccessPage";
import ProjectGroupPage from "../projects/settings/access/ProjectGroupPage";
import ProjectSettingsLayout from "../projects/settings/ProjectSettingsLayout";
import { GeneralTab, HistoryTab, OpenAccessTab, RulesTab } from "../projects/settings/SettingsTabs";
import ProjectIssuesPage from "../features/discussion/ProjectIssuesPage";
import { appPaths, ROUTE_PATTERNS } from "../shared/routing/appPaths";
import IssueReportPage from "../features/issueReports/IssueReportPage";
import LegacySectionRedirect from "./LegacySectionRedirect";
import PaneRedirect from "./PaneRedirect";
import { ItemEditAddress } from "../features/threads/prepare/author/EditAddressRedirect";
import NotFoundPage from "./NotFoundPage";
import RouteErrorPage from "./RouteErrorPage";
import NewTabPage from "../shell/workspace/NewTabPage";

async function loadHelpCenter() {
  return { Component: (await import("../features/help/view/HelpCenterPage")).default };
}

/** The window's route that is the workspace: every path the window's own pages don't take (F90). */
export const WORKSPACE_ROUTE_ID = "workspace";

/** The window's own pages, outside the workspace: signing in, setup, help. Paths come from `ROUTE_PATTERNS` only. */
export const WINDOW_ROUTES: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: ROUTE_PATTERNS.landing, element: <AuthCubeShell /> },
      { path: ROUTE_PATTERNS.login, element: <AuthCubeShell /> },
      { path: ROUTE_PATTERNS.firstAdministratorSetup, element: <FirstAdministratorSetupPage /> },
      { path: ROUTE_PATTERNS.invitationAcceptance, element: <InvitationAcceptancePage /> },
      { path: ROUTE_PATTERNS.waitingApproval, element: <WaitingForApprovalPage /> },
      { path: ROUTE_PATTERNS.verifyEmail, element: <VerifyEmailPage /> },
      { path: ROUTE_PATTERNS.forgotPassword, element: <ForgotPasswordPage /> },
      { path: ROUTE_PATTERNS.resetPassword, element: <ResetPasswordPage /> },
      { path: ROUTE_PATTERNS.register, element: <RegisterPage /> },
      // The help center (WA-6) is open without signing in, and loads only when opened.
      { path: ROUTE_PATTERNS.help, lazy: loadHelpCenter },
      { path: ROUTE_PATTERNS.helpPage, lazy: loadHelpCenter },
      { path: ROUTE_PATTERNS.legacyGuide, element: <LegacyGuideRedirect /> },
      // Signed in, `App` shows the workspace here: its tabs route `TAB_ROUTES` each on their own.
      { id: WORKSPACE_ROUTE_ID, path: "*" },
    ],
  },
];

/** The pages of a work tab (WA-10, F90): each tab routes them with a router of its own. */
export const TAB_ROUTES: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: ROUTE_PATTERNS.newTab, element: <NewTabPage /> },
      { path: ROUTE_PATTERNS.projects, element: <AllProjectsPage />, handle: helpHandle("projects") },
      { path: ROUTE_PATTERNS.issueReport, element: <IssueReportPage />, handle: asDetour({}) },
      {
        element: <ProjectSettingsLayout />,
        handle: asDetour(helpHandle("projectSettings"), appPaths.projects()),
        children: [
          { path: ROUTE_PATTERNS.projectSettings, element: <OpenAccessTab /> },
          { path: ROUTE_PATTERNS.projectSettingsAccess, element: <ProjectAccessPage /> },
          { path: ROUTE_PATTERNS.projectSettingsGroup, element: <ProjectGroupPage /> },
          { path: ROUTE_PATTERNS.projectSettingsRules, element: <RulesTab /> },
          { path: ROUTE_PATTERNS.projectSettingsHistory, element: <HistoryTab /> },
          { path: ROUTE_PATTERNS.projectSettingsGeneral, element: <GeneralTab /> },
        ],
      },
      { path: ROUTE_PATTERNS.projectIssues, element: <ProjectIssuesPage />, handle: asDetour(helpHandle("reviews")) },
      { path: ROUTE_PATTERNS.legacySources, element: <LegacySectionRedirect /> },
      { path: ROUTE_PATTERNS.legacyOntology, element: <LegacySectionRedirect /> },
      { path: ROUTE_PATTERNS.legacyData, element: <LegacySectionRedirect /> },
      { path: ROUTE_PATTERNS.sources, element: <SourcesIndexPage />, handle: helpHandle("documents") },
      { path: ROUTE_PATTERNS.sourcesImport, element: <SourcesIndexPage importing />, handle: helpHandle("importCorpus") },
      { path: ROUTE_PATTERNS.sourceDocument, element: <SourceDocumentPage />, handle: helpHandle("documents") },
      {
        path: ROUTE_PATTERNS.ontology,
        element: <OntologyPage />,
        children: [
          { index: true, element: <TermsPage />, handle: helpHandle("ontology") },
          { path: ROUTE_PATTERNS.ontologyVersion, element: <TermsPage />, handle: helpHandle("ontology") },
          { path: ROUTE_PATTERNS.ontologyTerm, element: <TermsPage />, handle: helpHandle("ontology") },
          // The Instances list went (H-21): what waits is in the review inbox, the rest on the lenses.
          { path: ROUTE_PATTERNS.instances, element: <PaneRedirect to={ROUTE_PATTERNS.threads} /> },
          { path: ROUTE_PATTERNS.extraction, element: <ExtractionPage />, handle: asDetour(helpHandle("data")) },
          { path: ROUTE_PATTERNS.formerChecks, element: <PaneRedirect to={ROUTE_PATTERNS.ontology} /> },
          // The regests prototype moved to Documents, as a project's presentation of a document (P-07).
          { path: ROUTE_PATTERNS.legacyRegests, element: <PaneRedirect to={ROUTE_PATTERNS.sources} /> },
          { path: ROUTE_PATTERNS.formerReleases, element: <PaneRedirect to={ROUTE_PATTERNS.ontology} /> },
          { path: ROUTE_PATTERNS.release, element: <ReleasePage />, handle: asDetour(helpHandle("ontology")) },
        ],
      },
      // The Memories page went: memories are kept in their own Threads (ADR-0031 D5).
      { path: ROUTE_PATTERNS.memories, element: <PaneRedirect to={ROUTE_PATTERNS.profileMemories} /> },
      { path: ROUTE_PATTERNS.threads, element: <ThreadsHome />, handle: helpHandle("threads") },
      { path: ROUTE_PATTERNS.threadPreparation, element: <ThreadPreparationPage />, handle: helpHandle("threads") },
      { path: ROUTE_PATTERNS.thread, element: <ThreadPage />, handle: helpHandle("threads") },
      { path: ROUTE_PATTERNS.runGraph, element: <RunGraphPage />, handle: helpHandle("threads") },
      { path: ROUTE_PATTERNS.threadHostOffer, element: <ThreadPage />, handle: helpHandle("threads") },
      { path: ROUTE_PATTERNS.batch, element: <BatchPage />, handle: helpHandle("batches") },
      { path: ROUTE_PATTERNS.batchVita, element: <BatchVitaPage />, handle: asDetour(helpHandle("batches"), ({ batchId = "" }) => appPaths.batch(batchId)) },
      { path: ROUTE_PATTERNS.workflows, element: <WorkflowsIndexPage />, handle: helpHandle("workflows") },
      // Authoring is a detour from the library: it leaves by the one Back (F87, W-11).
      { path: ROUTE_PATTERNS.newStep, element: <NewStepPage />, handle: asDetour(helpHandle("workflows")) },
      { path: ROUTE_PATTERNS.newArtifact, element: <NewArtifactPage />, handle: asDetour(helpHandle("workflows")) },
      { path: ROUTE_PATTERNS.newSkill, element: <NewSkillPage />, handle: asDetour(helpHandle("workflows")) },
      { path: ROUTE_PATTERNS.newLabelList, element: <NewLabelListPage />, handle: asDetour(helpHandle("workflows")) },
      { path: ROUTE_PATTERNS.newCapabilityOverrides, element: <NewCapabilityOverridesPage />, handle: asDetour(helpHandle("workflows")) },
      { path: ROUTE_PATTERNS.workflow, element: <WorkflowPage />, handle: helpHandle("workflows") },
      // The Reviews page went: the Threads list is the inbox (ADR-0031 D4).
      { path: ROUTE_PATTERNS.reviews, element: <PaneRedirect to={ROUTE_PATTERNS.threads} /> },
      { path: ROUTE_PATTERNS.review, element: <ReviewPage />, handle: asDetour(helpHandle("reviews")) },
      { path: ROUTE_PATTERNS.importProposal, element: <ImportProposalPage />, handle: asDetour(helpHandle("reviews"), appPaths.sources()) },
      { path: ROUTE_PATTERNS.item, element: <ItemPage />, handle: asDetour(helpHandle("workflows")) },
      { path: ROUTE_PATTERNS.itemEdit, element: <ItemEditAddress />, handle: asDetour(helpHandle("workflows")) },
      {
        element: <ProfileLayout />,
        handle: asDetour({}),
        children: [
          { path: ROUTE_PATTERNS.profile, element: <ProfilePage />, handle: helpHandle("profile") },
          { path: ROUTE_PATTERNS.profilePreferences, element: <PreferencesPage />, handle: helpHandle("preferences") },
          { path: ROUTE_PATTERNS.profileConnectors, element: <ConnectorsPage />, handle: helpHandle("connectors") },
          { path: ROUTE_PATTERNS.profileMemories, element: <MyMemoriesPage />, handle: helpHandle("memories") },
        ],
      },
      {
        element: <AdministrationLayout />,
        handle: asDetour({}),
        children: [
          { path: ROUTE_PATTERNS.administrationAccounts, element: <AccountsPage />, handle: helpHandle("administration") },
          { path: ROUTE_PATTERNS.administrationServerKeys, element: <ServerKeysPage />, handle: helpHandle("serverKeys") },
          { path: ROUTE_PATTERNS.administrationInstallation, element: <InstallationSettingsPage />, handle: helpHandle("administration") },
          { path: ROUTE_PATTERNS.administrationGroups, element: <InstallationGroupsPage />, handle: helpHandle("administration") },
          { path: ROUTE_PATTERNS.administrationGroup, element: <InstallationGroupPage />, handle: helpHandle("administration") },
          { path: ROUTE_PATTERNS.administrationDeletedProjects, element: <DeletedProjectsPage />, handle: helpHandle("administration") },
          { path: ROUTE_PATTERNS.administrationResearchSpace, element: <ResearchSpaceAdministrationPage />, handle: helpHandle("administration") },
        ],
      },
      { path: ROUTE_PATTERNS.connectorPairing, element: <ConnectorPairingPage />, handle: asDetour(helpHandle("connectors")) },
      { path: ROUTE_PATTERNS.connectionCallback, element: <ConnectionCallbackPage />, handle: asDetour(helpHandle("profile"), appPaths.profile()) },
      { path: ROUTE_PATTERNS.connectorSetup, element: <ConnectorSetupPage />, handle: asDetour(helpHandle("connectors")) },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
